// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Background firmware upload API — server-side uploads that survive navigation.
 * Uses `/api/firmware/background/*` and `/api/firmware/deployment-status` endpoints.
 *
 * Live callers only use deployment-status list/clear and cancel-all; other upload
 * helpers were removed after confirming zero frontend call sites.
 */

import axios from '../utils/axiosConfig';

// The axios request interceptor quietly cancels calls made while logged out
// (CanceledError('Not authenticated')) — that's expected during logout
// transitions, not an error worth logging.
function isCanceled(error) {
  return error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
}

class BackgroundFirmwareUploadApi {
  async cancelAllBackgroundUploads() {
    try {
      const response = await axios.post('/firmware/background/cancel-all', null, {
        timeout: 30000,
      });
      return response.data;
    } catch (error) {
      if (!isCanceled(error)) {
        console.error('[FIRMWARE] Failed to cancel all background uploads:', error.message);
      }
      return { success: false, error: error.message };
    }
  }

  async getDeploymentStatuses({ includeInactive = false } = {}) {
    try {
      const params = includeInactive ? { active_only: false } : {};
      const response = await axios.get('/firmware/deployment-status', {
        timeout: 10000,
        params,
      });
      return response.data;
    } catch (error) {
      if (!isCanceled(error)) {
        console.error('[FIRMWARE] Failed to get deployment statuses:', error.message);
      }
      return null;
    }
  }

  async clearDeploymentStatuses() {
    try {
      await axios.delete('/firmware/deployment-status', { timeout: 10000 });
    } catch (error) {
      if (!isCanceled(error)) {
        console.warn('[FIRMWARE] Failed to clear deployment statuses:', error.message);
      }
    }
  }
}

const backgroundFirmwareApi = new BackgroundFirmwareUploadApi();
export default backgroundFirmwareApi;
